import { useEffect, useState, useRef, useCallback } from "react";
import {
  getSupportedDevicesSync,
  huafendaConfigIdentifierMatchesDeviceId,
  normHuafendaIdentifier,
} from "@eweadn-qiankun/shared-utils";
import {
  ByHidFilter,
  DeviceDetector,
  Keyboard,
  ConnectType,
} from "@bytech/web-hid-sdk/keyboard";

export const useConnectDevice = (
  sdkApi: any,
  deviceType: "keyboard" | "mouse",
  sdkExample: any,
  useDevice: any
) => {
  const { sdkKeyboardBasicApi } = sdkApi.keyboard;
  const { serviceKeyboard, hfdServiceKeyboard, beiyingKeyboard } = sdkExample;
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectList, setConnectList] = useState<
    {
      device: any;
      configuration: any;
      deviceInfo: any;
    }[]
  >([]);
  const initialRef = useRef(false);
  const devicesKeyRef = useRef<any>(null);
  const beiyingUidRef = useRef<any>(null);
  const beiyingIs4GRef = useRef<boolean>(false);

  const onConnect = useCallback(
    async (
      // 是否初始化
      initial: boolean = true,
      // 连接方式，自己传过滤好的hid或者自动识别过滤
      connectType: "automatic" | "manual" = "automatic",
      // 配置项
      configuration?: any,
      // 适配方案商
      solutionRovider?: "xingshan" | "huafenda" | "beiying" | "jiangmeng",
      //过滤好的hid
      hidDevices?: any
    ) => {
      if (connectType === "automatic") {
        if (initial) {
          // 刷新页面时，自动获取已授权的设备
          const allDevices = await (navigator as any).hid.getDevices();
          const devicesData = groupDevices(allDevices);
          for (let i = 0; i < devicesData.length; i++) {
            onConnectSingle(devicesData[i]);
          }
        } else {
          // 手动选择的设备
          const devices = await (navigator as any).hid.requestDevice({
            filters: [],
          });
          if (!devices || devices?.length <= 0) {
            return;
          }
          onConnectSingle(devices);
        }
      } else {
        getSolutionProvider(hidDevices, configuration, solutionRovider);
      }
    },
    [deviceType, sdkApi]
  );

  // hid分组函数
  const groupDevices = useCallback((devices: any[]): any[][] => {
    const groupMap: any = {};
    devices.forEach((device) => {
      if (device.productId == null || device.vendorId == null) return;
      const groupKey = `${device.vendorId}-${device.productId}`;
      if (!groupMap[groupKey]) groupMap[groupKey] = [];
      groupMap[groupKey].push(device);
    });
    return Object.values(groupMap);
  }, []);

  // 单个设备连接
  const onConnectSingle = useCallback((devices: any) => {
    let solutionRovider = "";
    let configuration: any = null;
    for (let i = 0; i < devices.length; i++) {
      if (devicesKeyRef.current) {
        break;
      }
      const item: any = devices[i];
      const collections = item.collections;
      for (let j = 0; j < collections.length; j++) {
        if (devicesKeyRef.current) {
          break;
        }
        const items: any = collections[j];
        for (let k = 0; k < getSupportedDevicesSync()[deviceType].length; k++) {
          const adaptationItem: any = getSupportedDevicesSync()[deviceType][k];
          if (
            adaptationItem.productId === item.productId &&
            adaptationItem.vendorId === item.vendorId
          ) {
            configuration = adaptationItem;
            solutionRovider = adaptationItem.solutionRovider;
            if (
              solutionRovider === "huafenda" &&
              adaptationItem.adaptation.includes(items.usagePage)
            ) {
              devicesKeyRef.current = i;
              break;
            }
            if (solutionRovider === "beiying") {
              beiyingUidRef.current = adaptationItem.uuid[0];
              if (adaptationItem.is4G) {
                beiyingIs4GRef.current = true;
              }
            }
          }
        }
      }
    }
    getSolutionProvider(devices, configuration, solutionRovider);
  }, []);

  // 同步 connectList 到全局存储
  useEffect(() => {
    if (connectList && connectList.length) {
      console.log(connectList, "连接成功后set数据");
      useDevice.setAuthorizedDevices(connectList);
    }
  }, [connectList, useDevice]);

  // 从全局存储初始化 connectList
  useEffect(() => {
    const authorizedDevices = useDevice.getAuthorizedDevices || [];
    if (authorizedDevices.length) {
      console.log(authorizedDevices, "初始化已授权设备列表");
      setConnectList(authorizedDevices);
      setIsConnecting(false);
    }
  }, [useDevice.getAuthorizedDevices]);

  // HID 事件监听
  useEffect(() => {
    const hid = (navigator as any).hid;
    if (!hid) return;

    // 设备断开回调（直接定义在 useEffect 内，避免闭包捕获旧值）
    const handleDeviceDisconnect = async (event: any) => {
      const disconnectedDevice = event.device;
      if (!disconnectedDevice) return;

      // 关键：用函数式更新获取最新的 connectList（避开闭包陷阱）
      setConnectList((prevConnectList) => {
        // 找匹配设备的索引（productId + vendorId 双重匹配）
        const matchIndex = prevConnectList.findIndex(
          (item) =>
            item.configuration?.productId === disconnectedDevice.productId &&
            item.configuration?.vendorId === disconnectedDevice.vendorId
        );

        if (matchIndex === -1) return prevConnectList; // 无匹配项，直接返回原列表

        // 生成新数组（遵循 React 不可变原则）
        const updatedConnectList = [...prevConnectList];
        updatedConnectList.splice(matchIndex, 1);

        // 同步更新全局存储
        useDevice.setAuthorizedDevices(updatedConnectList);
        initialRef.current = false;
        console.log("设备已断开，最新列表：", updatedConnectList);
        return updatedConnectList;
      });
    };

    // 设备连接回调（按需保留原有逻辑）
    const handleDeviceConnect = async (event: any) => {
      const disconnectedDevice = event.device;
      if (!disconnectedDevice) return;
      if (!initialRef.current) {
        const devices = [disconnectedDevice];
        initialRef.current = true;
        let solutionRovider = "";
        let configuration: any = null;
        for (let i = 0; i < devices.length; i++) {
          if (devicesKeyRef.current) {
            break;
          }
          const item: any = devices[i];
          const collections = item.collections;
          for (let j = 0; j < collections.length; j++) {
            if (devicesKeyRef.current) {
              break;
            }
            const items: any = collections[j];
            for (
              let k = 0;
              k < getSupportedDevicesSync()[deviceType].length;
              k++
            ) {
              const adaptationItem: any =
                getSupportedDevicesSync()[deviceType][k];
              if (
                adaptationItem.productId === item.productId &&
                adaptationItem.vendorId === item.vendorId
              ) {
                configuration = adaptationItem;
                solutionRovider = adaptationItem.solutionRovider;
                if (
                  solutionRovider === "huafenda" &&
                  adaptationItem.adaptation.includes(items.usagePage)
                ) {
                  devicesKeyRef.current = i;
                  break;
                }
                if (solutionRovider === "beiying") {
                  beiyingUidRef.current = adaptationItem.uuid[0];
                  if (adaptationItem.is4G) {
                    beiyingIs4GRef.current = true;
                  }
                }
              }
            }
          }
        }
        getSolutionProvider(devices, configuration, solutionRovider);
      }
    };

    // 绑定监听
    hid.addEventListener("connect", handleDeviceConnect);
    hid.addEventListener("disconnect", handleDeviceDisconnect);

    // 组件卸载时解绑监听
    return () => {
      hid.removeEventListener("connect", handleDeviceConnect);
      hid.removeEventListener("disconnect", handleDeviceDisconnect);
    };
  }, [onConnect, useDevice]);

  const getSolutionProvider = useCallback(
    async (devices: any, configuration: any, solutionRovider: any) => {
      switch (solutionRovider) {
        case "xingshan":
          onXingshanConnect(devices, configuration);
          break;
        case "huafenda":
          onHuafendaConnect(devices, configuration);
          break;
        case "beiying":
          onBeiyingConnect(devices, configuration);
          break;
        case "jiangmeng":
          // onJiangmengConnect(devices, configuration);
          break;
      }
    },
    []
  );

  const onHuafendaConnect = useCallback(
    async (devices: any, configuration: any) => {
      await hfdServiceKeyboard.connectDevice(devices[devicesKeyRef.current]);
      await hfdServiceKeyboard.connect();
      const deviceId = await hfdServiceKeyboard.getDeviceId();
      const deviceInfo = await hfdServiceKeyboard.getDeviceInfo();
      const sdkId = normHuafendaIdentifier(deviceId);
      // 包含匹配：SDK `…:2:18` 命中配置裸 `…:2.4G Dongle`；多命中取更长
      const idHits = (
        getSupportedDevicesSync()[deviceType] as Array<Record<string, unknown>>
      )
        .filter((item) =>
          huafendaConfigIdentifierMatchesDeviceId(item.identifier, sdkId)
        )
        .sort(
          (a, b) =>
            normHuafendaIdentifier(b.identifier).length -
            normHuafendaIdentifier(a.identifier).length
        );
      if (idHits[0]) {
        configuration = {
          ...idHits[0],
          ...(sdkId ? { identifier: sdkId } : {}),
        };
      }
      if (deviceInfo) {
        devicesKeyRef.current = null;
        setIsConnecting(true);
        setConnectList((prevList) => [
          ...prevList,
          {
            device: devices[devicesKeyRef.current],
            deviceInfo: {
              ...deviceInfo,
              ...(sdkId ? { identifier: sdkId } : {}),
            },
            configuration,
          },
        ]);
      }
    },
    [deviceType, hfdServiceKeyboard]
  );

  const onXingshanConnect = useCallback(
    async (deviceInfo: any, configuration: any) => {
      console.log(deviceInfo, "deviceInfo");
      const xsDevice = await serviceKeyboard.getDevices();
      console.log(xsDevice, "xsDevicexsDevicexsDevice");
      if (xsDevice && xsDevice.length) {
        const { id } = xsDevice[0];
        const result: any = await sdkKeyboardBasicApi.init(id);
        if (result.code === 200) {
          devicesKeyRef.current = null;
          setIsConnecting(true);
          setConnectList((prevList) => [
            ...prevList,
            {
              device: xsDevice[0],
              deviceInfo: null,
              configuration,
            },
          ]);
        }
      }
    },
    [sdkKeyboardBasicApi, serviceKeyboard]
  );

  const onBeiyingConnect = useCallback(
    async (devices: any, configuration: any) => {
      const deviceDetector = new DeviceDetector(
        [new ByHidFilter(configuration.productId)],
        [beiyingUidRef.current]
      );
      const uuid2Device = await deviceDetector.getDevices();
      const beiyingDevices: any = uuid2Device.get(beiyingUidRef.current);
      let keyboards = null;
      if (beiyingIs4GRef.current) {
        keyboards = new Keyboard(beiyingDevices, {
          connectType: ConnectType.Wireless8K,
        });
      } else {
        keyboards = new Keyboard(beiyingDevices);
      }
      beiyingKeyboard.instance = keyboards;
      const deviceInfo = await beiyingKeyboard.instance.getDeviceInfo();
      devicesKeyRef.current = null;
      beiyingIs4GRef.current = false;
      beiyingUidRef.current = null;
      setIsConnecting(true);
      setConnectList((prevList) => [
        ...prevList,
        {
          device: devices,
          deviceInfo,
          configuration,
        },
      ]);
    },
    [beiyingKeyboard]
  );

  const onRetry = useCallback(async () => {}, []);

  return {
    isConnecting,
    connectList,
    onConnect,
    onRetry,
  };
};
